import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material';

interface InitialBalanceSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  initialBalance?: number;
  initialBalanceDate?: string;
  onSave: (balance: number, date: string) => void;
}

const InitialBalanceSettingsDrawer: React.FC<InitialBalanceSettingsDrawerProps> = ({
  open,
  onClose,
  initialBalance = 0,
  initialBalanceDate = '',
  onSave,
}) => {
  const [balance, setBalance] = useState<string>(initialBalance === 0 ? '' : initialBalance.toString());
  const [date, setDate] = useState<string>(initialBalanceDate);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setBalance(initialBalance === 0 ? '' : initialBalance.toString());
      setDate(initialBalanceDate);
    }
  }, [open, initialBalance, initialBalanceDate]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const balanceValue = parseFloat(balance) || 0;
      await onSave(balanceValue, date);
      onClose();
    } catch (error) {
      console.error('Błąd zapisywania salda początkowego:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Pozwól na puste pole lub liczby z kropką/przecinkiem
    if (value === '' || /^-?\d*[.,]?\d*$/.test(value)) {
      setBalance(value);
    }
  };

  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#d4d4d9' 
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#595966' 
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#2020AF !important' 
    },
    '& .MuiOutlinedInput-root.Mui-focused': { 
      boxShadow: 'none', 
      outline: 'none' 
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#d4d4d9' 
    },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#595966' 
    },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { 
      borderColor: '#2020AF !important' 
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ 
      fontFamily: 'Roboto, sans-serif', 
      fontWeight: 400, 
      fontSize: '14px', 
      lineHeight: '20px', 
      color: '#2D2D32', 
      mb: 0.5 
    }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 450, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ 
            fontFamily: 'Roboto, sans-serif', 
            fontWeight: 500, 
            fontSize: '18px', 
            color: '#2D2D32' 
          }}>
            Ustawienia salda początkowego
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>

        {/* Zawartość formularza */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12}>
              <FieldLabel text="Saldo początkowe" required />
              <TextField
                fullWidth
                placeholder="0,00"
                name="balance"
                value={balance}
                onChange={handleBalanceChange}
                required
                variant="outlined"
                size="small"
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ 
                        fontFamily: 'Roboto, sans-serif', 
                        fontSize: '16px', 
                        color: '#2D2D32' 
                      }}>
                        zł
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                helperText="To pole jest wymagane"
                error={balance === ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Data salda początkowego" required />
              <TextField
                fullWidth
                placeholder="rrrr-mm-dd"
                name="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                variant="outlined"
                size="small"
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input': {
                    color: date ? '#2D2D32' : '#B9B9B9'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#B9B9B9',
                    opacity: 1
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                    </InputAdornment>
                  )
                }}
                helperText="To pole jest wymagane"
                error={date === ''}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Stopka z przyciskami */}
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Box />
          <Box>
            <Button 
              onClick={onClose} 
              variant="text" 
              disabled={loading}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontWeight: 500, 
                fontSize: 14, 
                color: '#2D2D32', 
                textTransform: 'none' 
              }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading || balance === '' || date === ''}
              sx={{ 
                backgroundColor: '#020242',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              Zapisz
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InitialBalanceSettingsDrawer;

